import React, { useState, useEffect } from "react";
import {
  Box, Typography, Button, Grid, FormControl, FormLabel, TextField,
  Checkbox, FormControlLabel, IconButton, Select, MenuItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import styled from "styled-components";
import { postData } from "../../BaseUrl";
import Toast from "../../DynamicComponent/Toast";

const StyledTextField = styled(TextField)(() => ({
  "& .MuiOutlinedInput-root": {
    "& fieldset": { borderColor: "#D5D7DA", borderRadius: "10px" },
  },
}));

const labelStyle = { color: "#8F95B2", fontSize: "13px" };

const LEAVE_TYPES = ["Casual", "Medical", "Personal", "Mandate", "Other"];
const REASON_OPTIONS = ["Personal", "Medical", "Family Emergency", "Training", "Other"];

export default function MandateLeaveForm({ handleClose }) {
  const [form, setForm] = useState({
    email: sessionStorage.getItem("userEmail") || "",
    teacherName: sessionStorage.getItem("userFullName") || "",
    assignedNilai: "",
    teacherId: "",
    fromDate: "",
    toDate: "",
    leaveType: "",
    totalDays: "",
    reason: "",
    description: "",
  });
  const [declaration, setDeclaration] = useState(false);
  const [errors, setErrors] = useState({});
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });

  // Auto-calculate total days when fromDate/toDate change
  useEffect(() => {
    if (form.fromDate && form.toDate) {
      const from = new Date(form.fromDate);
      const to = new Date(form.toDate);
      if (to >= from) {
        const diff = Math.round((to - from) / (1000 * 60 * 60 * 24)) + 1;
        setForm((prev) => ({ ...prev, totalDays: String(diff) }));
      } else {
        setForm((prev) => ({ ...prev, totalDays: "" }));
      }
    }
  }, [form.fromDate, form.toDate]);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    if (errors[e.target.name]) setErrors((prev) => ({ ...prev, [e.target.name]: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const required = ["email", "teacherName", "fromDate", "toDate", "leaveType", "reason"];
    const newErrors = {};
    required.forEach((k) => { if (!form[k]) newErrors[k] = "Required"; });
    if (!declaration) newErrors.declaration = "You must agree to the declaration";
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    try {
      await postData("teacher/leave/create", { ...form });
      setToast({ open: true, severity: "success", message: "Leave applied successfully" });
      setTimeout(handleClose, 1200);
    } catch (_) {
      setToast({ open: true, severity: "error", message: "Failed to apply leave" });
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ p: "24px" }}>
      <Toast open={toast.open} handleClose={() => setToast({ ...toast, open: false })} severity={toast.severity} message={toast.message} />
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography sx={{ fontWeight: "bold", fontSize: "16px" }}>Apply Leave Form</Typography>
        <IconButton onClick={handleClose}><CloseIcon /></IconButton>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={6}>
          <FormControl fullWidth>
            <FormLabel sx={labelStyle}>Email</FormLabel>
            <StyledTextField size="small" name="email" type="email" value={form.email} onChange={handleChange} error={!!errors.email} helperText={errors.email} />
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl fullWidth>
            <FormLabel sx={labelStyle}>Teacher's Name</FormLabel>
            <StyledTextField size="small" name="teacherName" value={form.teacherName} onChange={handleChange} error={!!errors.teacherName} helperText={errors.teacherName} />
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl fullWidth>
            <FormLabel sx={labelStyle}>Assigned Nilai</FormLabel>
            <StyledTextField size="small" name="assignedNilai" value={form.assignedNilai} onChange={handleChange} placeholder="e.g. Nilai01" />
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl fullWidth>
            <FormLabel sx={labelStyle}>Teacher's ID</FormLabel>
            <StyledTextField size="small" name="teacherId" value={form.teacherId} onChange={handleChange} placeholder="e.g. EMP001" />
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl fullWidth>
            <FormLabel sx={labelStyle}>From Date</FormLabel>
            <StyledTextField size="small" name="fromDate" type="date" value={form.fromDate} onChange={handleChange} InputLabelProps={{ shrink: true }} error={!!errors.fromDate} helperText={errors.fromDate} />
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl fullWidth>
            <FormLabel sx={labelStyle}>To Date</FormLabel>
            <StyledTextField size="small" name="toDate" type="date" value={form.toDate} onChange={handleChange} InputLabelProps={{ shrink: true }} error={!!errors.toDate} helperText={errors.toDate} />
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl fullWidth>
            <FormLabel sx={labelStyle}>Leave Type</FormLabel>
            <Select size="small" name="leaveType" value={form.leaveType} onChange={handleChange} error={!!errors.leaveType} sx={{ borderRadius: "10px" }}>
              {LEAVE_TYPES.map((lt) => <MenuItem key={lt} value={lt}>{lt}</MenuItem>)}
            </Select>
            {errors.leaveType && <Typography sx={{ fontSize: "11px", color: "#f44336", mt: 0.5 }}>{errors.leaveType}</Typography>}
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl fullWidth>
            <FormLabel sx={labelStyle}>Total Days</FormLabel>
            <StyledTextField size="small" name="totalDays" type="number" value={form.totalDays} onChange={handleChange} inputProps={{ min: 1 }} placeholder="Auto-calculated" />
          </FormControl>
        </Grid>
        <Grid item xs={6}>
          <FormControl fullWidth>
            <FormLabel sx={labelStyle}>Reason</FormLabel>
            <Select size="small" name="reason" value={form.reason} onChange={handleChange} error={!!errors.reason} sx={{ borderRadius: "10px" }}>
              {REASON_OPTIONS.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
            </Select>
            {errors.reason && <Typography sx={{ fontSize: "11px", color: "#f44336", mt: 0.5 }}>{errors.reason}</Typography>}
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <FormControl fullWidth>
            <FormLabel sx={labelStyle}>Others (Description)</FormLabel>
            <StyledTextField size="small" name="description" multiline rows={3} value={form.description} onChange={handleChange} placeholder="Describe the reason in detail..." />
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={declaration}
                onChange={(e) => setDeclaration(e.target.checked)}
                sx={{ color: errors.declaration ? "#f44336" : undefined }}
              />
            }
            label={
              <Typography sx={{ fontSize: "13px", color: errors.declaration ? "#f44336" : "#555" }}>
                Declaration of Agreement to Abide by the Absence Policy.
              </Typography>
            }
          />
          {errors.declaration && (
            <Typography sx={{ fontSize: "11px", color: "#f44336", ml: 1.5 }}>{errors.declaration}</Typography>
          )}
        </Grid>
      </Grid>

      <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 3 }}>
        <Button onClick={handleClose} sx={{ color: "#414651", textTransform: "none", px: 4, borderRadius: "10px", border: "1px solid #D5D7DA" }}>Cancel</Button>
        <Button type="submit" sx={{ background: "linear-gradient(90deg,#4A7BA8,#244C77)", color: "#fff", textTransform: "none", px: 4, borderRadius: "10px" }}>Submit</Button>
      </Box>
    </Box>
  );
}
